import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  Typography,
  IconButton,
  Sheet,
  Alert,
} from '@mui/joy';
import { Plus as Add, Trash as Delete, DotsSixVertical as DragIndicator, FloppyDisk as Save, ArrowLeft as ArrowBack } from '@phosphor-icons/react';
import { apiClient } from '../../lib/api';
import { ChecklistsAPI } from '../../lib/apiChecklists';
import type { Checklist, ChecklistStep } from '../../types/checklists';

const ChecklistEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [checklist, setChecklist] = useState<Partial<Checklist>>({
    title: '',
    description: '',
    show_in_mobile_app: false,
    steps: [],
  });
  
  const [preview, setPreview] = useState(false);
  const [draftNotice, setDraftNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!isEditing) return;
    
    setLoading(true);
    try {
      const data = await ChecklistsAPI.retrieve(parseInt(id!));
      setChecklist({ ...data, ...(data.draft || {}) });
    } catch (error) {
      console.error('Failed to load checklist:', error);
      setError('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (draftOnly = false) => {
    if (!checklist.title?.trim()) {
      setError('Title is required');
      return;
    }

    // Validate steps
    const steps = checklist.steps || [];
    for (let i = 0; i < steps.length; i++) {
      if (!steps[i].title?.trim()) {
        setError(`Step ${i + 1} title is required`);
        return;
      }
    }

    setSaving(true);
    setError(null);
    
    try {
      // Ensure steps have correct order values
      const stepsWithOrder = steps.map((step, index) => ({
        ...step,
        order: index + 1,
      }));

      const payload = {
        ...checklist,
        steps: stepsWithOrder,
      };

      if (isEditing) {
        await apiClient.patch(`/api/checklists/checklists/${id}/draft/`, payload);
        if (!draftOnly) await apiClient.post(`/api/checklists/checklists/${id}/publish/`, {});
      } else {
        await ChecklistsAPI.create(payload);
      }
      
      if (draftOnly) { setError(null); setDraftNotice('Draft saved. New runs continue using the published revision.'); }
      else navigate('/checklists');
    } catch (error) {
      console.error('Failed to save checklist:', error);
      setError('Failed to save checklist');
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    const newStep: ChecklistStep = {
      title: '',
      description: '',
      reference_link: '',
      order: (checklist.steps?.length || 0) + 1,
      photos_required: false,
      allow_skip: false,
    };
    
    setChecklist({
      ...checklist,
      steps: [...(checklist.steps || []), newStep],
    });
  };

  const updateStep = (index: number, updates: Partial<ChecklistStep>) => {
    const steps = [...(checklist.steps || [])];
    steps[index] = { ...steps[index], ...updates };
    setChecklist({ ...checklist, steps });
  };

  const deleteStep = (index: number) => {
    const steps = [...(checklist.steps || [])];
    steps.splice(index, 1);
    setChecklist({ ...checklist, steps });
  };

  const moveStep = (fromIndex: number, toIndex: number) => {
    const steps = [...(checklist.steps || [])];
    const [removed] = steps.splice(fromIndex, 1);
    steps.splice(toIndex, 0, removed);
    setChecklist({ ...checklist, steps });
  };

  useEffect(() => {
    load();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography level="body-sm">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, width: '100%', maxWidth: 2000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/checklists')} variant="plain">
          <ArrowBack />
        </IconButton>
        <Typography level="h3">
          {isEditing ? 'Edit Checklist' : 'New Checklist'}
        </Typography>
      </Box>

      {draftNotice && <Alert color="success" sx={{ mb: 2 }}>{draftNotice}</Alert>}
      {isEditing && <Alert sx={{ mb: 2 }}>Published revision {checklist.revision || 1}. Editing these steps does not change existing executions. Review the steps below before publishing.</Alert>}
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ p: 3, mb: 3, width: '100%' }}>
        <Typography level="title-md" sx={{ mb: 2 }}>Checklist Details</Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl required>
            <FormLabel>Title</FormLabel>
            <Input
              value={checklist.title || ''}
              onChange={(e) => setChecklist({ ...checklist, title: e.target.value })}
              placeholder="Enter checklist title..."
            />
          </FormControl>

          <FormControl><FormLabel>Category</FormLabel><Input value={checklist.category || ''} onChange={e => setChecklist({ ...checklist, category: e.target.value })} /></FormControl>
          <FormControl><FormLabel>Tags (comma separated)</FormLabel><Input value={(checklist.tags || []).join(', ')} onChange={e => setChecklist({ ...checklist, tags: e.target.value.split(',').map(v => v.trim()) })} /></FormControl>
          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              value={checklist.description || ''}
              onChange={(e) => setChecklist({ ...checklist, description: e.target.value })}
              placeholder="Enter checklist description..."
              minRows={3}
            />
          </FormControl>

          <FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormLabel>Show in Mobile App</FormLabel>
              <Switch
                checked={checklist.show_in_mobile_app || false}
                onChange={(e) => setChecklist({ ...checklist, show_in_mobile_app: e.target.checked })}
              />
            </Box>
          </FormControl>
        </Box>
      </Card>

      <Card sx={{ p: 3, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography level="title-md">Steps</Typography>
          <Button
            startDecorator={<Add />}
            onClick={addStep}
            variant="soft"
          >
            Add Step
          </Button>
        </Box>

        {!checklist.steps?.length ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography level="body-sm">
              No steps added yet. Click "Add Step" to get started.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {checklist.steps.map((step, index) => (
              <Sheet key={index} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <IconButton
                      size="sm"
                      variant="plain"
                      aria-label="Move step up"
                      disabled={index === 0}
                      onClick={() => moveStep(index, index - 1)}
                    >
                      ↑
                    </IconButton>
                    <DragIndicator size={18} color="var(--joy-palette-text-tertiary)" />
                    <IconButton
                      size="sm"
                      variant="plain"
                      aria-label="Move step down"
                      disabled={index === (checklist.steps?.length || 0) - 1}
                      onClick={() => moveStep(index, index + 1)}
                    >
                      ↓
                    </IconButton>
                  </Box>

                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography level="title-sm">Step {index + 1}</Typography>
                      <Box sx={{ flex: 1 }} />
                      <IconButton
                        size="sm"
                        color="danger"
                        variant="plain"
                        aria-label={`Remove step ${index + 1}`}
                        onClick={() => deleteStep(index)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>

                    <FormControl required>
                      <FormLabel>Title</FormLabel>
                      <Input
                        value={step.title || ''}
                        onChange={(e) => updateStep(index, { title: e.target.value })}
                        placeholder="Enter step title..."
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Description</FormLabel>
                      <Textarea
                        value={step.description || ''}
                        onChange={(e) => updateStep(index, { description: e.target.value })}
                        placeholder="Enter step description..."
                        minRows={2}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Reference Link</FormLabel>
                      <Input
                        value={step.reference_link || ''}
                        onChange={(e) => updateStep(index, { reference_link: e.target.value })}
                        placeholder="Enter reference URL (optional)..."
                      />
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <FormControl>
                        <FormLabel>Photos Required</FormLabel>
                        <Switch
                          checked={step.photos_required || false}
                          onChange={(e) => updateStep(index, { photos_required: e.target.checked })}
                          size="sm"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Allow Skip</FormLabel>
                        <Switch
                          checked={step.allow_skip || false}
                          onChange={(e) => updateStep(index, { allow_skip: e.target.checked })}
                          size="sm"
                        />
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
              </Sheet>
            ))}
          </Box>
        )}
      </Card>

      {preview && <Card sx={{ mt: 2 }}><Typography level="h3">Revision preview</Typography><Typography level="h4">{checklist.title}</Typography><Typography>{checklist.description}</Typography>{checklist.steps?.map((step, index) => <Sheet key={index} variant="soft" sx={{ p: 2, borderRadius: 'sm' }}><Typography level="title-md">{index + 1}. {step.title}</Typography><Typography>{step.description}</Typography><Typography level="body-sm">{step.photos_required ? 'Photo required' : 'Photo optional'} · {step.allow_skip ? 'May skip with a reason' : 'Must complete'}</Typography>{step.reference_link && <a href={step.reference_link} target="_blank" rel="noopener noreferrer">Reference material</a>}</Sheet>)}</Card>}
      <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={() => setPreview(!preview)}>{preview ? 'Hide preview' : 'Preview revision'}</Button>
        {isEditing && <Button variant="outlined" loading={saving} onClick={() => handleSave(true)}>Save draft</Button>}
        <Button
          onClick={() => handleSave()}
          loading={saving}
          startDecorator={<Save />}
          disabled={!checklist.title?.trim()}
        >
          {isEditing ? 'Publish revision' : 'Create checklist'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={() => navigate('/checklists')}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default ChecklistEditorPage;